import React, { useState, useEffect } from 'react';
import { turso } from '@/database/client';

const AdminActivateAccount: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const ADMIN_PASSWORD = 'whipt2026admin';

  useEffect(() => {
    const adminAuth = sessionStorage.getItem('whipt_admin_auth');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('whipt_admin_auth', 'true');
      setIsAuthenticated(true);
    } else {
      setResult({ type: 'error', message: '❌ Incorrect password' });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl p-8">
          <h1 className="text-3xl font-bold text-purple-300 mb-2">🔒 Admin Access</h1>
          <p className="text-slate-300 mb-6">Enter admin password to continue</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="w-full px-4 py-3 bg-[#2d1b4e] border border-purple-500/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-xs text-purple-300 mt-2 hover:text-purple-200"
              >
                {showPassword ? '🙈 Hide' : '👁️ Show'} password
              </button>
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold text-white transition-colors"
            >
              🔓 Unlock
            </button>
            {result && result.type === 'error' && (
              <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200 text-sm">
                {result.message}
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  const activateAccount = async () => {
    if (!email.trim()) {
      setResult({ type: 'error', message: '❌ Please enter an email address' });
      return;
    }

    setLoading(true);
    setResult({ type: 'info', message: '🔄 Processing...' });

    try {
      // 1. Find user by email
      const userResult = await turso.execute({
        sql: 'SELECT id, email, name FROM users WHERE email = ? LIMIT 1',
        args: [email.trim()]
      });

      if (!userResult.rows || userResult.rows.length === 0) {
        setResult({ type: 'error', message: `❌ No user found with email: ${email}` });
        setLoading(false);
        return;
      }

      const userId = userResult.rows[0].id as string;
      const userName = userResult.rows[0].name as string;

      // 2. Get current age verification
      const ageDataResult = await turso.execute({
        sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
        args: [userId]
      });

      let restrictions: any;
      let isNewFormat = false;

      if (!ageDataResult.rows || ageDataResult.rows.length === 0) {
        // No age verification exists - create new one for under-13
        restrictions = {
          ageGroup: 'under13',
          birthdate: '2015-01-01', // Default
          verifiedAt: new Date().toISOString(),
          canAccessMessaging: false,
          canAccessMusic: false,
          canAccessVideoRooms: false,
          canAccessSocialFeatures: false,
          requiresParentalConsent: true,
          parentalConsentStatus: 'approved',
          parentalConsentApproved: true,
          parentalConsentDate: new Date().toISOString(),
          accountActive: true
        };
        isNewFormat = true;
      } else {
        restrictions = JSON.parse(ageDataResult.rows[0].data_value as string);
        
        // Check if old format (has "age" field instead of "ageGroup")
        if (restrictions.age && !restrictions.ageGroup) {
          // Convert old format to new format
          restrictions = {
            ageGroup: 'under13',
            birthdate: '2015-01-01', // Default since we don't have it
            verifiedAt: new Date().toISOString(),
            canAccessMessaging: false,
            canAccessMusic: false,
            canAccessVideoRooms: false,
            canAccessSocialFeatures: false,
            requiresParentalConsent: true,
            parentalConsentStatus: 'approved',
            parentalConsentApproved: true,
            parentalConsentDate: new Date().toISOString(),
            accountActive: true
          };
          isNewFormat = true;
        } else {
          // New format already - just activate
          if (restrictions.accountActive === true) {
            setResult({ 
              type: 'success', 
              message: `✅ Account already active!\n\nUser: ${userName}\nEmail: ${email}\nAge Group: ${restrictions.ageGroup}\nStatus: Active` 
            });
            setLoading(false);
            return;
          }
          
          restrictions.accountActive = true;
          restrictions.parentalConsentApproved = true;
          restrictions.parentalConsentStatus = 'approved';
          restrictions.parentalConsentDate = new Date().toISOString();
        }
      }

      // 3. Save to database

      // 3. Save to database
      if (isNewFormat || !ageDataResult.rows || ageDataResult.rows.length === 0) {
        // Delete old entry if exists, then insert new
        await turso.execute({
          sql: `DELETE FROM user_data WHERE user_id = ? AND data_type = 'age_verification'`,
          args: [userId]
        });
        
        await turso.execute({
          sql: `
            INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
            VALUES (?, ?, 'age_verification', 'restrictions', ?, datetime('now'), datetime('now'))
          `,
          args: [
            `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userId,
            JSON.stringify(restrictions)
          ]
        });
      } else {
        // Update existing
        await turso.execute({
          sql: `
            UPDATE user_data 
            SET data_value = ?, updated_at = datetime('now')
            WHERE user_id = ? AND data_type = 'age_verification'
          `,
          args: [JSON.stringify(restrictions), userId]
        });
      }

      setResult({ 
        type: 'success', 
        message: `✅ ACCOUNT ACTIVATED!\n\nUser: ${userName}\nEmail: ${email}\nUser ID: ${userId}\nAge Group: ${restrictions.ageGroup}\nBirthdate: ${restrictions.birthdate}\n\n${isNewFormat ? 'Converted from old format to new format\n' : ''}Account Status: ACTIVE\nConsent Status: APPROVED\nActivated: ${new Date().toLocaleString()}\n\nThe user can now sign in!` 
      });

    } catch (error: any) {
      setResult({ 
        type: 'error', 
        message: `❌ ERROR:\n\n${error.message}\n\n${error.stack}` 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl p-8">
        <h1 className="text-3xl font-bold text-purple-300 mb-2">🔧 Admin - Activate Account</h1>
        <p className="text-slate-300 mb-6">Use this tool to manually activate accounts after parental consent.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-200 mb-2">
              Child's Email Address:
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="child@example.com"
              className="w-full px-4 py-3 bg-[#2d1b4e] border border-purple-500/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-400"
              disabled={loading}
            />
          </div>

          <button
            onClick={activateAccount}
            disabled={loading}
            className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-900 disabled:cursor-not-allowed rounded-lg font-bold text-white transition-colors"
          >
            {loading ? '⏳ Processing...' : '✅ Activate Account'}
          </button>

          {result && (
            <div className={`p-4 rounded-lg whitespace-pre-wrap font-mono text-sm ${
              result.type === 'success' ? 'bg-green-900/30 border border-green-500/50 text-green-200' :
              result.type === 'error' ? 'bg-red-900/30 border border-red-500/50 text-red-200' :
              'bg-blue-900/30 border border-blue-500/50 text-blue-200'
            }`}>
              {result.message}
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-purple-500/20">
          <p className="text-xs text-slate-400">
            <strong>Note:</strong> This tool bypasses the consent email system and directly activates accounts in the database. 
            Use only for testing or when consent has been verified through other means.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminActivateAccount;
